import type { TradeSessionRegularHours } from '@/utils/tradeSessionStatsDisplay';

export type VolumeByPriceDateSlice = {
  date: string;
  volume: number;
};

export type VolumeByPriceBar = {
  price: number;
  volume: number;
  buy_volume?: number;
  sell_volume?: number;
  neutral_volume?: number;
  by_date?: VolumeByPriceDateSlice[];
};

/** Max horizontal extent of profile bars as a fraction of plot width. */
export const VOLUME_BY_PRICE_MAX_WIDTH_FRAC = 0.36;

/** Yearly / multi-day volume profiles use wider bins than single-session tape stats. */
export const YEARLY_VOLUME_BY_PRICE_BUCKET_CENTS = 25;

/** Finer bins for low-priced tickers (e.g. SVCO ~$5–15 vs PLTR ~$100+). */
export const YEARLY_VOLUME_BY_PRICE_BUCKET_CENTS_LOW = 1;

/** Below this spot price (USD), yearly profiles use {@link YEARLY_VOLUME_BY_PRICE_BUCKET_CENTS_LOW}. */
export const YEARLY_VOLUME_LOW_PRICE_THRESHOLD_USD = 50;

export function resolveYearlyVolumeByPriceBucketCents(
  referencePriceUsd: number | null | undefined,
): number {
  if (
    referencePriceUsd != null &&
    Number.isFinite(referencePriceUsd) &&
    referencePriceUsd > 0 &&
    referencePriceUsd < YEARLY_VOLUME_LOW_PRICE_THRESHOLD_USD
  ) {
    return YEARLY_VOLUME_BY_PRICE_BUCKET_CENTS_LOW;
  }
  return YEARLY_VOLUME_BY_PRICE_BUCKET_CENTS;
}

export const VOLUME_BY_PRICE_FILL = '#38bdf8';
export const VOLUME_BY_PRICE_FILL_OPACITY = 0.10;

export const VOLUME_BY_PRICE_BUY_FILL = '#10b981';
export const VOLUME_BY_PRICE_SELL_FILL = '#f43f5e';
export const VOLUME_BY_PRICE_NEUTRAL_FILL = '#94a3b8';
export const VOLUME_BY_PRICE_SIDE_FILL_OPACITY = 0.14;

export type VolumeByPriceColorMode = 'total' | 'bySide' | 'byDate';

/** Merged tail dates when a price bin has more slices than the overlay cap. */
export const VOLUME_BY_PRICE_OTHER_DATE = '__other__';

export const VOLUME_BY_PRICE_DATE_FILL_OPACITY = 0.2;
export const VOLUME_BY_PRICE_DATE_HIGHLIGHT_OPACITY = 0.38;
export const VOLUME_BY_PRICE_OTHER_FILL = '#94a3b8';

/** Chronological palette (oldest → newest within the visible range). */
export const VOLUME_BY_PRICE_DATE_PALETTE = [
  '#0284c7',
  '#7c3aed',
  '#059669',
  '#d97706',
  '#db2777',
  '#0891b2',
  '#4f46e5',
  '#65a30d',
  '#ea580c',
  '#be123c',
  '#0d9488',
  '#a16207',
] as const;

/** Max horizontal segments per price bin (remaining volume → "other"). */
export const VOLUME_BY_PRICE_MAX_DATE_SEGMENTS = 12;

/** Extra right margin on the chart when the layer is active (room for bars + Y ticks). */
export const VOLUME_BY_PRICE_CHART_RIGHT_MARGIN_PX = 12;

/**
 * Cent-level session histogram bars from trade_session_stats regular_hours.
 */
export function volumeByPriceBarsFromRegularHours(
  rth: TradeSessionRegularHours | null | undefined,
): VolumeByPriceBar[] | null {
  const hist = rth?.price_histogram;
  if (!hist?.by_volume) return null;

  const bars: VolumeByPriceBar[] = [];
  for (const [key, rawVol] of Object.entries(hist.by_volume)) {
    const cent = Number.parseInt(key, 10);
    const volume = Number(rawVol);
    if (!Number.isFinite(cent) || !Number.isFinite(volume) || volume <= 0) continue;
    bars.push({ price: cent / 100, volume });
  }
  return bars.length ? bars : null;
}

export function volumeByPriceBucketCents(
  rth: TradeSessionRegularHours | null | undefined,
): number {
  const cents = rth?.price_histogram?.bucket_cents;
  return typeof cents === 'number' && cents > 0 ? cents : 1;
}

export function volumeByPriceSessionLabel(
  symbol: string,
  date: string,
): string {
  return `Volume by price · ${symbol} · ${date}`;
}

export type YearlyPriceVolumePayload = {
  success?: boolean;
  symbol: string;
  start_date: string;
  end_date: string;
  session?: string;
  bucket_cents?: number;
  total_volume?: number;
  unique_price_levels?: number;
  days_included_count?: number;
  has_side_attribution?: boolean;
  has_date_attribution?: boolean;
  days_included?: string[];
  price_levels: VolumeByPriceBar[];
};

export function volumeByPriceBarsFromYearlyPayload(
  payload: YearlyPriceVolumePayload | null | undefined,
): VolumeByPriceBar[] | null {
  const levels = payload?.price_levels;
  if (!Array.isArray(levels) || levels.length === 0) return null;
  const bars: VolumeByPriceBar[] = [];
  for (const row of levels) {
    const price = Number(row?.price);
    const volume = Number(row?.volume);
    if (!Number.isFinite(price) || !Number.isFinite(volume) || volume <= 0) continue;
    const bar: VolumeByPriceBar = { price, volume };
    const buy = Number(row?.buy_volume);
    const sell = Number(row?.sell_volume);
    const neutral = Number(row?.neutral_volume);
    if (Number.isFinite(buy) && buy > 0) bar.buy_volume = buy;
    if (Number.isFinite(sell) && sell > 0) bar.sell_volume = sell;
    if (Number.isFinite(neutral) && neutral > 0) bar.neutral_volume = neutral;
    const byDate = row?.by_date;
    if (Array.isArray(byDate) && byDate.length > 0) {
      const slices: VolumeByPriceDateSlice[] = [];
      for (const slice of byDate) {
        const date = String(slice?.date ?? '').trim();
        const sliceVol = Number(slice?.volume);
        if (!date || !Number.isFinite(sliceVol) || sliceVol <= 0) continue;
        slices.push({ date, volume: sliceVol });
      }
      if (slices.length) bar.by_date = slices;
    }
    bars.push(bar);
  }
  return bars.length ? bars : null;
}

export function volumeByPriceRangeLabel(
  symbol: string,
  startDate: string,
  endDate: string,
): string {
  if (startDate === endDate) {
    return volumeByPriceSessionLabel(symbol, startDate);
  }
  return `Volume by price · ${symbol} · ${startDate} – ${endDate}`;
}

/**
 * Prefer an explicit selection, else the newest visible session that has tape stats.
 */
export function resolveVolumeByPriceTargetDate(
  visibleDates: string[],
  statDates: string[],
  selectedDate?: string | null,
): string | null {
  const statSet = new Set(statDates);
  if (selectedDate) {
    if (statSet.size === 0 || statSet.has(selectedDate)) return selectedDate;
  }
  for (let i = visibleDates.length - 1; i >= 0; i--) {
    const d = visibleDates[i];
    if (d && statSet.has(d)) return d;
  }
  if (selectedDate) return selectedDate;
  return visibleDates.length ? visibleDates[visibleDates.length - 1] ?? null : null;
}

export function tradeSessionStatsHasVolumeHistogram(
  stats: { regular_hours?: TradeSessionRegularHours } | null | undefined,
): boolean {
  const byVol = stats?.regular_hours?.price_histogram?.by_volume;
  return !!byVol && Object.keys(byVol).length > 0;
}

export function volumeByPriceHasSideAttribution(
  payload: YearlyPriceVolumePayload | null | undefined,
  bars: VolumeByPriceBar[] | null | undefined,
): boolean {
  if (payload?.has_side_attribution) return true;
  if (!bars?.length) return false;
  return bars.some(
    (b) => (b.buy_volume ?? 0) > 0 || (b.sell_volume ?? 0) > 0 || (b.neutral_volume ?? 0) > 0,
  );
}

export function volumeByPriceHasDateAttribution(
  payload: YearlyPriceVolumePayload | null | undefined,
  bars: VolumeByPriceBar[] | null | undefined,
): boolean {
  if (payload?.has_date_attribution) return true;
  if (!bars?.length) return false;
  return bars.some((b) => (b.by_date?.length ?? 0) > 1);
}

/** Trading days in range (chronological), for stable segment colors. */
export function volumeByPriceDateOrderFromPayload(
  payload: YearlyPriceVolumePayload | null | undefined,
  bars: VolumeByPriceBar[] | null | undefined,
): string[] {
  const fromPayload = payload?.days_included;
  if (Array.isArray(fromPayload) && fromPayload.length > 0) {
    return [...fromPayload].sort();
  }
  const dates = new Set<string>();
  for (const bar of bars ?? []) {
    for (const slice of bar.by_date ?? []) {
      if (slice.date && slice.date !== VOLUME_BY_PRICE_OTHER_DATE) {
        dates.add(slice.date);
      }
    }
  }
  return [...dates].sort();
}

export function volumeByPriceColorForDate(
  date: string,
  dateOrder: string[],
): string {
  if (date === VOLUME_BY_PRICE_OTHER_DATE) return VOLUME_BY_PRICE_OTHER_FILL;
  const idx = dateOrder.indexOf(date);
  const paletteIdx = idx >= 0 ? idx : 0;
  return VOLUME_BY_PRICE_DATE_PALETTE[paletteIdx % VOLUME_BY_PRICE_DATE_PALETTE.length];
}

/**
 * Keep the largest date slices; roll the rest into {@link VOLUME_BY_PRICE_OTHER_DATE}.
 */
export function capVolumeByPriceDateSegments(
  segments: VolumeByPriceDateSlice[],
  maxSegments: number = VOLUME_BY_PRICE_MAX_DATE_SEGMENTS,
): VolumeByPriceDateSlice[] {
  if (segments.length <= maxSegments) {
    return [...segments].sort((a, b) => a.date.localeCompare(b.date));
  }
  const sorted = [...segments].sort((a, b) => b.volume - a.volume);
  const kept = sorted.slice(0, maxSegments - 1);
  const otherVol = sorted.slice(maxSegments - 1).reduce((sum, s) => sum + s.volume, 0);
  const out = [...kept];
  if (otherVol > 0) {
    out.push({ date: VOLUME_BY_PRICE_OTHER_DATE, volume: otherVol });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

export function formatVolumeByPriceDateLabel(date: string): string {
  if (date === VOLUME_BY_PRICE_OTHER_DATE) return 'Other days';
  return date;
}
